import { useState, useEffect } from 'react';
import { Truck, AlertTriangle, Gauge, Activity, Loader2, Search, TrendingUp, Droplet, Battery, Fuel, Clock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { getVehicleDiagnosticsFromDatabase, getVehicleDiagnostics } from '../../services/samsaraService';
import { getVehicleLocations } from '../../services/locationService';

interface TruckDiagnostics {
  id: string;
  unitNumber: string;
  samsaraId: string;
  make?: string;
  model?: string;
  year?: number;
  vin?: string;
  // Real-time data from Samsara
  speed: number;
  heading?: number;
  latitude?: number;
  longitude?: number;
  lastUpdate: string;
  // Diagnostic data from Samsara
  odometer?: number; // miles
  engineHours?: number; // hours
  fuelPercent?: number; // percentage
  defPercent?: number; // DEF level percentage
  batteryVoltage?: number; // volts
  checkEngineLight?: 'none' | 'amber' | 'red';
  // Status
  status: 'active' | 'idle' | 'offline';
  faults: Array<{
    id: string;
    severity: 'low' | 'medium' | 'high';
    description: string;
    spn: number;
    fmi: number;
    count: number;
    activeSince?: string;
  }>;
}

export function SamsaraDiagnosticsView() {
  const toast = useToast();
  const [trucks, setTrucks] = useState<TruckDiagnostics[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'idle' | 'offline'>('all');
  const [selectedTruck, setSelectedTruck] = useState<TruckDiagnostics | null>(null);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);

  useEffect(() => {
    loadTruckDiagnostics();
  }, []);

  // Load diagnostics for a specific truck when clicked
  const loadTruckDiagnosticsDetail = async (truck: TruckDiagnostics) => {
    setSelectedTruck(truck);
    setLoadingDiagnostics(true);

    try {
      // Try database first (webhook data)
      let diagResponse = await getVehicleDiagnosticsFromDatabase(truck.id);
      let diag = diagResponse.diagnostics || {};
      
      // If no webhook data yet, fall back to API
      if (!diag.odometer && !diag.engineHours && !diag.fuelPercent) {
        const apiResponse = await getVehicleDiagnostics(truck.samsaraId);
        diag = apiResponse.diagnostics || {};
      }

      // Update the selected truck with diagnostics
      setSelectedTruck({
        ...truck,
        odometer: diag.odometer,
        engineHours: diag.engineHours,
        fuelPercent: diag.fuelPercent,
        defPercent: diag.defPercent,
        batteryVoltage: diag.batteryVoltage,
        checkEngineLight: diag.checkEngineLight || 'none',
        faults: diag.faults || [],
      });
    } catch (error: any) {
      console.error('Error loading truck diagnostics:', error);
      toast.error('Failed to load diagnostics');
    } finally {
      setLoadingDiagnostics(false);
    }
  };

  const loadTruckDiagnostics = async () => {
    try {
      setLoading(true);

      // Fetch trucks from database
      const { data: trucksData, error: trucksError } = await supabase
        .from('trucks')
        .select('id, unit_number, samsara_id, make, model, year, vin, status')
        .not('samsara_id', 'is', null)
        .order('unit_number');

      if (trucksError) throw trucksError;

      // Fetch real-time locations from Samsara
      const locationsResponse = await getVehicleLocations();

      if (!locationsResponse.success || !locationsResponse.locations) {
        throw new Error('Failed to fetch Samsara data');
      }

      // Map trucks with Samsara data - fetch diagnostics for each
      const diagnosticsPromises = (trucksData || []).map(async truck => {
        const location = locationsResponse.locations?.find(
          loc => loc.vehicleId === truck.samsara_id
        );

        // Determine status based on last update
        let status: 'active' | 'idle' | 'offline' = 'offline';
        if (location) {
          const lastUpdateTime = new Date(location.time);
          const now = new Date();
          const hoursAgo = (now.getTime() - lastUpdateTime.getTime()) / (1000 * 60 * 60);

          if (hoursAgo < 1) {
            status = location.speed && location.speed > 5 ? 'active' : 'idle';
          } else if (hoursAgo < 24) {
            status = 'idle';
          }
        }

        // Don't fetch diagnostics on initial load - only when truck is clicked
        // This prevents making 276 API calls (2 per truck) on page load
        return {
          id: truck.id,
          unitNumber: truck.unit_number,
          samsaraId: truck.samsara_id,
          make: truck.make || undefined,
          model: truck.model || undefined,
          year: truck.year || undefined,
          vin: truck.vin || undefined,
          speed: location?.speed || 0,
          heading: location?.heading,
          latitude: location?.latitude,
          longitude: location?.longitude,
          lastUpdate: location?.time || 'No data',
          status,
          // Diagnostics will be loaded on-demand when truck is clicked
          odometer: undefined,
          engineHours: undefined,
          fuelPercent: undefined,
          defPercent: undefined,
          batteryVoltage: undefined,
          checkEngineLight: 'none' as 'none' | 'amber' | 'red',
          faults: [],
        };
      });

      const diagnostics = await Promise.all(diagnosticsPromises);
      setTrucks(diagnostics);
    } catch (error: any) {
      console.error('Error loading truck diagnostics:', error);
      toast.error('Failed to load truck diagnostics');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'idle':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'offline':
        return 'bg-slate-100 text-slate-700 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <Activity className="w-4 h-4" />;
      case 'idle':
        return <Gauge className="w-4 h-4" />;
      case 'offline':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Gauge className="w-4 h-4" />;
    }
  };

  const formatLastUpdate = (timestamp: string) => {
    if (timestamp === 'No data') return timestamp;
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  // Filter trucks
  const filteredTrucks = trucks.filter(truck => {
    const matchesSearch = 
      truck.unitNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      truck.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      truck.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      truck.vin?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || truck.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Samsara Vehicle Diagnostics</h2>
            <p className="text-blue-100">
              Real-time vehicle data, diagnostics, and fault codes from Samsara.
            </p>
            <p className="text-blue-200 text-sm mt-2 flex items-center gap-2">
              <span className="bg-blue-500 bg-opacity-30 px-2 py-1 rounded">👆 Click any truck row</span>
              to view detailed diagnostics (odometer, fuel, DEF, battery, faults)
            </p>
          </div>
          <div className="bg-green-500 bg-opacity-20 border border-green-300 rounded-lg px-4 py-2">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-green-100">Live Data</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Trucks</p>
              <p className="text-2xl font-bold text-slate-900">{trucks.length}</p>
            </div>
            <Truck className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-green-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600">Active</p>
              <p className="text-2xl font-bold text-green-700">
                {trucks.filter(t => t.status === 'active').length}
              </p>
            </div>
            <Activity className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-yellow-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600">Idle</p>
              <p className="text-2xl font-bold text-yellow-700">
                {trucks.filter(t => t.status === 'idle').length}
              </p>
            </div>
            <Gauge className="w-8 h-8 text-yellow-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Offline</p>
              <p className="text-2xl font-bold text-slate-700">
                {trucks.filter(t => t.status === 'offline').length}
              </p>
            </div>
            <AlertTriangle className="w-8 h-8 text-slate-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by truck, make, model, or VIN..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
          />
        </div>

        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
              statusFilter === 'all'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setStatusFilter('active')}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
              statusFilter === 'active'
                ? 'bg-white text-green-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setStatusFilter('idle')}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
              statusFilter === 'idle'
                ? 'bg-white text-yellow-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Idle
          </button>
          <button
            onClick={() => setStatusFilter('offline')}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
              statusFilter === 'offline'
                ? 'bg-white text-slate-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Offline
          </button>
        </div>
      </div>

      {/* Trucks Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Truck</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Last Update</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-slate-600 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredTrucks.map((truck) => (
                <tr 
                  key={truck.id} 
                  onClick={() => loadTruckDiagnosticsDetail(truck)}
                  className="hover:bg-blue-50 hover:shadow-md transition-all cursor-pointer group"
                  title="Click to view detailed diagnostics"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-2.5 rounded-lg shadow-sm">
                        <Truck className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-bold text-slate-900 text-base">{truck.unitNumber}</div>
                        {truck.make && truck.model && (
                          <p className="text-sm text-slate-600 mt-0.5">
                            {truck.year} {truck.make} {truck.model}
                          </p>
                        )}
                        {truck.vin && (
                          <p className="text-xs text-slate-400 mt-0.5">VIN: {truck.vin}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${
                      truck.status === 'active' 
                        ? 'bg-green-50 border-green-200 text-green-700' 
                        : truck.status === 'idle'
                        ? 'bg-yellow-50 border-yellow-200 text-yellow-700'
                        : 'bg-slate-50 border-slate-200 text-slate-600'
                    }`}>
                      <div className={`w-2 h-2 rounded-full ${
                        truck.status === 'active' ? 'bg-green-500 animate-pulse' :
                        truck.status === 'idle' ? 'bg-yellow-500' :
                        'bg-slate-400'
                      }`}></div>
                      <span className="text-sm font-medium capitalize">{truck.status}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-600">
                      {truck.lastUpdate !== 'No data' 
                        ? new Date(truck.lastUpdate).toLocaleString()
                        : 'No data'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        loadTruckDiagnosticsDetail(truck);
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm shadow-sm"
                    >
                      <Gauge className="w-4 h-4" />
                      View Diagnostics
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">📊 About Samsara Diagnostics</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Real-time data</strong> from Samsara GPS and telematics devices</li>
          <li>• <strong>Check Engine Light</strong>: Red (critical), Amber (warning), or OK</li>
          <li>• <strong>Odometer readings</strong> in miles from ECU</li>
          <li>• <strong>Engine Hours</strong> total runtime tracking</li>
          <li>• <strong>Fuel Level</strong> percentage with color indicators</li>
          <li>• <strong>DEF Level</strong> (Diesel Exhaust Fluid) monitoring</li>
          <li>• <strong>Battery Voltage</strong> health monitoring</li>
          <li>• <strong>Active Faults</strong>: Click to view detailed diagnostic trouble codes (DTCs)</li>
        </ul>
      </div>

      {/* Diagnostics Details Modal */}
      {selectedTruck && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedTruck(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">Diagnostics - {selectedTruck.unitNumber}</h3>
                  <p className="text-blue-100 mt-1">
                    {selectedTruck.year} {selectedTruck.make} {selectedTruck.model}
                  </p>
                  {selectedTruck.vin && (
                    <p className="text-blue-200 text-sm mt-1">VIN: {selectedTruck.vin}</p>
                  )}
                </div>
                <button
                  onClick={() => setSelectedTruck(null)}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Loading State */}
              {loadingDiagnostics && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                  <span className="ml-3 text-slate-600">Loading diagnostics...</span>
                </div>
              )}

              {/* Diagnostics Content */}
              {!loadingDiagnostics && (
              <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {/* Engine Hours */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-slate-600" />
                    <h4 className="font-semibold text-slate-700">Engine Hours</h4>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {selectedTruck.engineHours ? `${selectedTruck.engineHours.toLocaleString()} hrs` : '-'}
                  </p>
                </div>

                {/* Check Engine Light */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-slate-600" />
                    <h4 className="font-semibold text-slate-700">Check Engine</h4>
                  </div>
                  <p className={`text-lg font-bold ${
                    selectedTruck.checkEngineLight === 'red' ? 'text-red-600' :
                    selectedTruck.checkEngineLight === 'amber' ? 'text-amber-600' :
                    'text-green-600'
                  }`}>
                    {selectedTruck.checkEngineLight === 'red' ? 'Red Warning' :
                     selectedTruck.checkEngineLight === 'amber' ? 'Amber Warning' :
                     '✓ OK'}
                  </p>
                </div>

                {/* Odometer */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-slate-600" />
                    <h4 className="font-semibold text-slate-700">Odometer (ECU)</h4>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {selectedTruck.odometer ? `${selectedTruck.odometer.toLocaleString()} mi` : '-'}
                  </p>
                </div>

                {/* Fuel */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Fuel className="w-5 h-5 text-slate-600" />
                    <h4 className="font-semibold text-slate-700">Fuel</h4>
                  </div>
                  <p className={`text-2xl font-bold ${
                    selectedTruck.fuelPercent !== undefined && selectedTruck.fuelPercent < 25 ? 'text-red-600' :
                    selectedTruck.fuelPercent !== undefined && selectedTruck.fuelPercent < 50 ? 'text-yellow-600' :
                    'text-green-600'
                  }`}>
                    {selectedTruck.fuelPercent !== undefined ? `${selectedTruck.fuelPercent}%` : '-'}
                  </p>
                </div>

                {/* DEF Level */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Droplet className="w-5 h-5 text-slate-600" />
                    <h4 className="font-semibold text-slate-700">DEF Level</h4>
                  </div>
                  <p className={`text-2xl font-bold ${
                    selectedTruck.defPercent !== undefined && selectedTruck.defPercent < 25 ? 'text-red-600' :
                    selectedTruck.defPercent !== undefined && selectedTruck.defPercent < 50 ? 'text-yellow-600' :
                    'text-blue-600'
                  }`}>
                    {selectedTruck.defPercent !== undefined ? `${selectedTruck.defPercent}%` : '-'}
                  </p>
                </div>

                {/* Battery Voltage */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Battery className="w-5 h-5 text-slate-600" />
                    <h4 className="font-semibold text-slate-700">Battery Voltage</h4>
                  </div>
                  <p className={`text-2xl font-bold ${
                    selectedTruck.batteryVoltage !== undefined && selectedTruck.batteryVoltage < 11 ? 'text-red-600' :
                    selectedTruck.batteryVoltage !== undefined && selectedTruck.batteryVoltage < 12 ? 'text-yellow-600' :
                    'text-green-600'
                  }`}>
                    {selectedTruck.batteryVoltage !== undefined ? `${selectedTruck.batteryVoltage.toFixed(3)} V` : '-'}
                  </p>
                </div>
              </div>

              {/* Active Faults Section */}
              <div className="border-t border-slate-200 pt-6">
                <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  Active Faults {selectedTruck.faults.length > 0 && `(${selectedTruck.faults.length})`}
                </h4>
                
                {selectedTruck.faults.length > 0 ? (
                  <div className="space-y-3">
                    {selectedTruck.faults.map((fault) => (
                      <div
                        key={fault.id}
                        className={`border rounded-lg p-4 ${
                          fault.severity === 'high' ? 'border-red-300 bg-red-50' :
                          fault.severity === 'medium' ? 'border-amber-300 bg-amber-50' :
                          'border-yellow-300 bg-yellow-50'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              fault.severity === 'high' ? 'bg-red-200 text-red-800' :
                              fault.severity === 'medium' ? 'bg-amber-200 text-amber-800' :
                              'bg-yellow-200 text-yellow-800'
                            }`}>
                              {fault.severity.toUpperCase()} SEVERITY
                            </span>
                            <span className="text-sm text-slate-600">Count: {fault.count}</span>
                          </div>
                        </div>
                        
                        <h5 className="font-semibold text-slate-900 mb-1">
                          SPN: {fault.spn} – FMI: {fault.fmi}
                        </h5>
                        
                        <p className="text-sm text-slate-700 mb-2">
                          {fault.description}
                        </p>
                        
                        {fault.activeSince && (
                          <p className="text-xs text-slate-500">
                            {fault.activeSince}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <p className="text-green-700 font-semibold">✓ No Active Faults</p>
                    <p className="text-green-600 text-sm mt-1">All systems operating normally</p>
                  </div>
                )}
              </div>
              </>
            )}
            </div>
            
            <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
              <button
                onClick={() => setSelectedTruck(null)}
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
